// client/src/components/command/JobProgress.jsx
// SSE-powered progress bar component for background jobs.

import React from "react";

const styles = {
    container: {
        background: "linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%)",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid rgba(139, 92, 246, 0.15)",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
    },
    title: {
        color: "#e2e8f0",
        fontSize: "14px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    badge: (color) => ({
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "9999px",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        background: color === "green" ? "rgba(16, 185, 129, 0.15)" :
            color === "yellow" ? "rgba(245, 158, 11, 0.15)" :
                color === "red" ? "rgba(239, 68, 68, 0.15)" :
                    "rgba(139, 92, 246, 0.15)",
        color: color === "green" ? "#10b981" :
            color === "yellow" ? "#f59e0b" :
                color === "red" ? "#ef4444" :
                    "#8b5cf6",
    }),
    barTrack: {
        height: "8px",
        borderRadius: "9999px",
        background: "rgba(255,255,255,0.06)",
        overflow: "hidden",
    },
    barFill: (pct, color) => ({
        height: "100%",
        borderRadius: "9999px",
        width: `${Math.min(pct, 100)}%`,
        background: `linear-gradient(90deg, ${color}cc, ${color})`,
        transition: "width 0.5s ease",
    }),
    stats: {
        display: "flex",
        gap: "20px",
        marginTop: "12px",
        flexWrap: "wrap",
    },
    stat: {
        color: "#94a3b8",
        fontSize: "12px",
    },
    statValue: {
        color: "#e2e8f0",
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
    },
    sseIndicator: (connected) => ({
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: connected ? "#10b981" : "#ef4444",
        boxShadow: connected ? "0 0 6px #10b981" : "none",
        display: "inline-block",
    }),
};

function statusBadge(status) {
    const map = {
        running: { label: "Running", color: "green" },
        paused: { label: "Paused", color: "yellow" },
        done: { label: "Done", color: "green" },
        failed: { label: "Failed", color: "red" },
        pending: { label: "Pending", color: "purple" },
    };
    const { label, color } = map[status] || { label: status, color: "purple" };
    return <span style={styles.badge(color)}>{label}</span>;
}

export default function JobProgress({ job, sseConnected, type = "fetch" }) {
    if (!job) return null;

    const total = job.total || 0;
    const processed = job.processed || 0;
    const pct = total > 0 ? (processed / total) * 100 : (job.status === "done" ? 100 : 0);
    const barColor = job.status === "failed" ? "#ef4444" : "#8b5cf6";

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.title}>
                    <span style={styles.sseIndicator(sseConnected)} title={sseConnected ? "SSE Connected" : "Polling"} />
                    {type === "fetch" ? "📥 Fetch Progress" : type === "validate" ? "🔍 Validation Progress" : "⚡ Execution Progress"}
                </div>
                {statusBadge(job.status)}
            </div>

            <div style={styles.barTrack}>
                <div style={styles.barFill(pct, barColor)} />
            </div>

            <div style={styles.stats}>
                <span style={styles.stat}>Processed: <span style={styles.statValue}>{processed.toLocaleString()}</span>{total > 0 && ` / ${total.toLocaleString()}`}</span>
                {job.pages > 0 && <span style={styles.stat}>Pages: <span style={styles.statValue}>{job.pages}</span></span>}
                {(job.succeeded != null) && <span style={styles.stat}>✓ <span style={{ ...styles.statValue, color: "#10b981" }}>{job.succeeded}</span></span>}
                {(job.failed != null && job.failed > 0) && <span style={styles.stat}>✗ <span style={{ ...styles.statValue, color: "#ef4444" }}>{job.failed}</span></span>}
                <span style={styles.stat}>{pct.toFixed(1)}%</span>
            </div>
        </div>
    );
}
